// path: src/StoryMode.js

import React, { useMemo, useState } from "react";
import "./App.css";
import StoryLevelMap from "./Components/story/StoryLevelMap";
import OverlayTopBar from "./Components/OverlayTopBar";
import AppFooter from "./Components/AppFooter";
import {
    getStoryProgressSummary,
    isStoryHardModeActive,
    isStoryHardModeUnlocked,
    loadStoryProgress,
} from "./utils/storyProgress";
import { isStoryLevelUnlocked } from "./utils/storyProgress";
import { applyStoryHardMode, buildStoryRunConfig } from "./utils/runConfig";

const StoryMode = ({
    progress: progressProp,
    onPlayStoryLevel,
    onToggleHardMode,
    onBackHome,
    onHome,
    onProfile,
    onStore,
    onTreats,
    onPrivacyPolicy,
    onTermsOfService,
    storeUnlocked = false,
    treats = 0,
    initialMapPage = "surface",
}) => {
    const [liveMessage, setLiveMessage] = useState("");

    const progress = useMemo(() => {
        return progressProp || loadStoryProgress();
    }, [progressProp]);

    const summary = getStoryProgressSummary(progress);
    const hardModeUnlocked = isStoryHardModeUnlocked(progress);
    const hardModeActive = isStoryHardModeActive(progress);

    const handlePlayLevel = (levelId) => {
        if (!isStoryLevelUnlocked(levelId, progress)) {
            setLiveMessage(`Story Level ${levelId} is still locked.`);
            return;
        }

        let runConfig = buildStoryRunConfig(levelId);

        if (hardModeActive && runConfig) {
            runConfig = applyStoryHardMode(runConfig);
        }

        if (!runConfig || typeof onPlayStoryLevel !== "function") {
            setLiveMessage("Unable to start that Story level.");
            return;
        }

        setLiveMessage(
            hardModeActive
                ? `Starting Story Level ${levelId} in Hard Mode.`
                : `Starting Story Level ${levelId}.`
        );
        onPlayStoryLevel(runConfig);
    };

    const header = (
        <header className="storyModeOverlay storyModeOverlay--top">
            <OverlayTopBar
                onHome={onHome}
                onProfile={onProfile}
                onStore={onStore}
                onTreats={onTreats}
                onBack={onBackHome}
                storeUnlocked={storeUnlocked}
                treats={treats}
            />
            <div className="storyModeOverlay__titleRow">
                <h1 className="impactFont storyModeTitle">Story Mode</h1>
            </div>
            <p className="storyModeSubtitle">
                {hardModeActive
                    ? "Hard Mode: replay the path with Hardcore rules. Checks mark Hard clears only."
                    : summary.underworldUnlocked
                    ? "Follow the Overworld path or brave the fiery Underworld bonus stages."
                    : "Tap a stage on the path from beach to space."}
            </p>
            {hardModeUnlocked ? (
                <label className="storyHardModeToggle">
                    <input
                        type="checkbox"
                        checked={hardModeActive}
                        onChange={(event) => {
                            if (typeof onToggleHardMode === "function") {
                                onToggleHardMode(event.target.checked);
                            }
                        }}
                    />
                    Hard Mode
                </label>
            ) : null}
        </header>
    );

    return (
        <div className="storyModeScreen">
            <StoryLevelMap
                progress={progress}
                onPlayLevel={handlePlayLevel}
                header={header}
                initialMapPage={initialMapPage}
            />

            <div className="srOnly" role="status" aria-live="polite">
                {liveMessage}
            </div>
            <AppFooter
                variant="overlay"
                onPrivacyPolicy={onPrivacyPolicy}
                onTermsOfService={onTermsOfService}
            />
        </div>
    );
};

export default StoryMode;
